import React, { FunctionComponent, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  Autocomplete,
  Card,
  CardContent,
  Fade,
  Grid,
  Stack,
  Typography,
} from '@mui/material';

import {
  adminUpdateLessonHistoryTrialConversion,
  ILessonHistory,
} from '../../../../../../../../../../store/V2/LessonHistorySlice';
import {
  ISelectedOption,
  tutorPaymentTrialConversationStatusOptions,
  TZoomMeetingSectionPaymentTypeOption,
  zoomMeetingSectionPaymentTypeOptions,
} from '../../../../../../../../../../constants';
import { TPTextField } from '../../../../../../../../../../components/Inputs';
import { capitalizeFirstLetter } from '../../../../../../../../../../helper';
import DarkLoadingButton, {
  defaultButtonStyles,
} from '../../../../../../../../../../components/V2/Buttons';
import { ZOOM_MEETING_SECTION_PAYMENT_TYPES } from '../../../../../../../../../../store/V2/SectionSlice';

interface TrialConversionTabProps {
  lessonHistory: ILessonHistory;
  refreshListEnabled?: boolean;
  refreshList?: () => void;
}

type TState = {
  trialStatusOption: ISelectedOption | null;
  paymentType: TZoomMeetingSectionPaymentTypeOption | null;
};

const CustomContainer = ({ children }) => {
  return (
    <Card
      elevation={0}
      sx={{ backgroundColor: '#F8FAFC', borderRadius: '8px' }}
    >
      <CardContent style={{ padding: '16px' }}>{children}</CardContent>
    </Card>
  );
};

const initialState: TState = {
  trialStatusOption: null,
  paymentType: null,
};

const TrialConversionTab: FunctionComponent<TrialConversionTabProps> = ({
  lessonHistory,
  refreshListEnabled = false,
  refreshList,
}) => {
  const dispatch = useDispatch();

  // State
  const [state, setState] = useState<TState>({ ...initialState });
  const [isLoading, setIsLoading] = useState(false);

  const isSubmitDisabled = !state.trialStatusOption || !state.paymentType;

  useEffect(() => {
    setState((prevState) => {
      const newState = { ...prevState };

      if ('isTrialConverted' in lessonHistory) {
        let paymentTypeOption = zoomMeetingSectionPaymentTypeOptions.find(
          (item) =>
            item.value === ZOOM_MEETING_SECTION_PAYMENT_TYPES.NON_BILLABLE
        );

        if (lessonHistory.isTrialConverted) {
          paymentTypeOption = zoomMeetingSectionPaymentTypeOptions.find(
            (item) => item.value === ZOOM_MEETING_SECTION_PAYMENT_TYPES.BILLABLE
          );
        }

        if (paymentTypeOption) {
          newState.paymentType = paymentTypeOption;
        }
      }

      if (lessonHistory.trialConversionStatus) {
        const matchingOption = tutorPaymentTrialConversationStatusOptions.find(
          (item) => item.value === lessonHistory.trialConversionStatus
        );

        if (matchingOption) {
          newState.trialStatusOption = matchingOption;
        }
      }

      return newState;
    });
  }, []);

  const updateStateAttributes = (attributes: Partial<TState>) => {
    setState((prevState) => ({ ...prevState, ...attributes }));
  };

  const handleSubmit = async () => {
    setIsLoading(true);

    if (state.paymentType && state.trialStatusOption) {
      await dispatch(
        adminUpdateLessonHistoryTrialConversion({
          lessonHistoryId: lessonHistory._id,
          isTrialConverted:
            state.paymentType.value ===
            ZOOM_MEETING_SECTION_PAYMENT_TYPES.BILLABLE,
          trialStatus: state.trialStatusOption.value,
        })
      );
    }

    if (refreshListEnabled && refreshList) {
      refreshList();
    }

    setIsLoading(false);
  };

  return (
    <Fade in>
      <div>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <CustomContainer>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography fontSize="16px" fontWeight={500} color="#647488">
                  Trial Status
                </Typography>
                <div
                  style={{
                    minWidth: '200px',
                    width: '400px',
                    maxWidth: '350px',
                  }}
                >
                  <Autocomplete
                    fullWidth
                    size="small"
                    value={state.trialStatusOption}
                    options={tutorPaymentTrialConversationStatusOptions}
                    getOptionLabel={(option) => option.label}
                    isOptionEqualToValue={(option, value) =>
                      option.value === value.value
                    }
                    onChange={(event, value) =>
                      updateStateAttributes({
                        trialStatusOption: value,
                      })
                    }
                    renderInput={(params) => (
                      <TPTextField
                        {...params}
                        placeholder="Trial Status"
                        InputProps={{
                          ...params.InputProps,
                          sx: { backgroundColor: '#fff' },
                        }}
                      />
                    )}
                  />
                </div>
              </Stack>
            </CustomContainer>
          </Grid>
          <Grid item xs={12}>
            <CustomContainer>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography fontSize="16px" fontWeight={500} color="#647488">
                  Payment Type
                </Typography>
                <div
                  style={{
                    minWidth: '200px',
                    maxWidth: '200px',
                  }}
                >
                  <Autocomplete
                    fullWidth
                    size="small"
                    value={state.paymentType}
                    options={zoomMeetingSectionPaymentTypeOptions}
                    getOptionLabel={(option) =>
                      capitalizeFirstLetter(
                        option.label.toLowerCase().replace('_', ' ')
                      )
                    }
                    isOptionEqualToValue={(option, value) =>
                      option.value === value.value
                    }
                    onChange={(event, value) =>
                      updateStateAttributes({
                        paymentType: value,
                      })
                    }
                    renderInput={(params) => (
                      <TPTextField
                        {...params}
                        placeholder="Payment Type"
                        InputProps={{
                          ...params.InputProps,
                          sx: { backgroundColor: '#fff' },
                        }}
                      />
                    )}
                  />
                </div>
              </Stack>
            </CustomContainer>
          </Grid>
          <Grid item xs={12}>
            <Stack direction="row" justifyContent="flex-end">
              <DarkLoadingButton
                variant="contained"
                color="inherit"
                loading={isLoading}
                disabled={isSubmitDisabled}
                onClick={handleSubmit}
                sx={{ ...defaultButtonStyles }}
              >
                Save
              </DarkLoadingButton>
            </Stack>
          </Grid>
        </Grid>
      </div>
    </Fade>
  );
};

export default TrialConversionTab;
